// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

/* This file provides MSAL auth configuration to get access token through nested app authentication. */

/* global console */

import { PublicClientNext, type IPublicClientApplication } from "@azure/msal-browser";

export { AccountManager };

export const PERMISSION = [
  "user.read",
  "openid",
  "profile",
  "Calendars.Read.Shared"
]

// Define type for injected config
export interface AuthConfig {
  clientId: string;
  tenantId: string;
  authority: string;
  version?: string;
  enableFindColleague?: boolean;
  enableOrderInClassic?: boolean;
  enableAIAgent?: boolean;
  endpointUrl?: string;
}

// Fallback values for development mode (when running locally without API)
const fallbackConfig: AuthConfig = {
  clientId: "43d2b2f6-b906-421c-bc40-a5b1b5aa571f",
  tenantId: "1be5b0cb-0e02-4548-be48-57b7a5c71228",
  authority: "https://login.microsoftonline.com/1be5b0cb-0e02-4548-be48-57b7a5c71228",
};

// Get config from window object (injected by API middleware) or use fallback
export const getAuthConfig = (): AuthConfig => {
  const windowConfig = (window as any).AUTH_CONFIG as AuthConfig | undefined;
  console.log("🚀 ~ getAuthConfig ~ windowConfig:", windowConfig)
  
  if (windowConfig && windowConfig.clientId && windowConfig.tenantId) {
    // Validate that we have required values
    return {
      clientId: windowConfig.clientId,
      tenantId: windowConfig.tenantId,
      authority: windowConfig.authority || `https://login.microsoftonline.com/${windowConfig.tenantId}`,
      version: windowConfig.version,
      enableFindColleague: windowConfig.enableFindColleague ?? false,
      enableOrderInClassic: windowConfig.enableOrderInClassic ?? false,
      enableAIAgent: windowConfig.enableAIAgent ?? false,
      endpointUrl: windowConfig.endpointUrl || "",
    };
  }
  
  // Fallback to default values (for development mode)
  console.warn("Using fallback auth config. Config should be injected by API middleware in production.");
  return fallbackConfig;
};

const authConfig = getAuthConfig();

const msalConfig = {
  auth: {
    clientId: authConfig.clientId,
    authority: authConfig.authority,
    supportsNestedAppAuth: true,
  },
};

// Encapsulate functions for getting user account and token information.
class AccountManager {
  loginHint: string = "";
  pca: IPublicClientApplication | undefined = undefined;
  isInitializing: boolean = false;
  initializationPromise: Promise<void> | null = null;

  // Initialize MSAL public client application.
  async initialize(loginHint: string) {
    // Nếu đã khởi tạo rồi thì không cần khởi tạo lại
    if (this.pca !== undefined) {
      console.log("AccountManager already initialized");
      return;
    }

    // Nếu đang trong quá trình khởi tạo, trả về promise đang chạy
    if (this.isInitializing && this.initializationPromise) {
      console.log("AccountManager initialization in progress, waiting...");
      return this.initializationPromise;
    }

    // Bắt đầu khởi tạo
    this.isInitializing = true;

    this.initializationPromise = new Promise<void>(async (resolve, reject) => {
      try {
        console.log("Initializing AccountManager with MSAL...");
        this.loginHint = loginHint;
        this.pca = await PublicClientNext.createPublicClientApplication(msalConfig);
        console.log("AccountManager initialized successfully");
        this.isInitializing = false;
        resolve();
      } catch (error) {
        console.error("Failed to initialize AccountManager:", error);
        this.isInitializing = false;
        this.initializationPromise = null;
        reject(error);
      }
    });

    return this.initializationPromise;
  }

  /**
   * 
   * @param scopes the minimum scopes needed.
   * @returns An access token.
   */
  async ssoGetToken(scopes: string[]) {
    const userAccount = await this.ssoGetUserIdentity(scopes);
    return userAccount.accessToken;
  }

  /**
   * 
   * Uses MSAL and nested app authentication to get the user account from Office SSO.
   * This demonstrates how to work with user identity from the token.
   * 
   * @param scopes The minimum scopes needed.
   * @returns The user account data (including identity).
   */
  async ssoGetUserIdentity(scopes: string[]) {
    // Kiểm tra và đợi khởi tạo nếu cần
    if (this.pca === undefined) {
      if (this.isInitializing && this.initializationPromise) {
        console.log("Waiting for AccountManager initialization to complete...");
        await this.initializationPromise;
      } else {
        throw new Error("AccountManager is not initialized! Please call initialize() first.");
      }
    }

    // Đảm bảo this.pca đã được khởi tạo sau khi chờ
    if (this.pca === undefined) {
      throw new Error("AccountManager initialization failed");
    }

    // Specify minimum scopes needed for the access token.
    const tokenRequest = {
      scopes: scopes,
      loginHint: this.loginHint,
    };

    try {
      console.log("Trying to acquire token silently...");

      //acquireTokenSilent requires an active account. Check if one exists, otherwise use ssoSilent.
      const account = this.pca.getActiveAccount();
      const userAccount = account ? await this.pca.acquireTokenSilent(tokenRequest) : await this.pca.ssoSilent(tokenRequest);

      console.log("Acquired token silently.");
      return userAccount;
    } catch (error) {
      console.log(`Unable to acquire token silently: ${error}`);
    }

    // Đảm bảo this.pca vẫn được khởi tạo
    if (this.pca === undefined) {
      throw new Error("AccountManager has lost initialization");
    }

    // Acquire token silent failure. Send an interactive request via popup.
    try {
      console.log("Trying to acquire token interactively...");
      const userAccount = await this.pca.acquireTokenPopup(tokenRequest);
      console.log("Acquired token interactively.");
      return userAccount;
    } catch (popupError) {
      // Acquire token interactive failure.
      console.log(`Unable to acquire token interactively: ${popupError}`);
      throw new Error(`Unable to acquire access token: ${popupError}`);
    }
  }
}
